/**
 * Browser Google Picker for choosing one existing Drive folder. Picking grants
 * holito per-file (drive.file) access to that folder for the owner's connection;
 * the server then validates and stores only the folder id and name.
 */
import type { GooglePickerSession } from '../../lib/integrations/storage/picker-config';

/** Google's documented browser API loader; the Picker library loads through gapi. */
const GOOGLE_API_SCRIPT = 'https://apis.google.com/js/api.js';
const FOLDER_MIME = 'application/vnd.google-apps.folder';
/** Bound a hung library download so the control never spins forever. */
const LOAD_TIMEOUT_MS = 20_000;

/** The subset of a Picker DocsView this module configures. */
export interface GooglePickerDocsView {
  setIncludeFolders(included: boolean): GooglePickerDocsView;
  setSelectFolderEnabled(enabled: boolean): GooglePickerDocsView;
  setMimeTypes(mimeTypes: string): GooglePickerDocsView;
  setEnableDrives(enabled: boolean): GooglePickerDocsView;
}

/** One picked Drive item as reported by the Picker. */
export interface GooglePickerDocument { id?: string; name?: string; mimeType?: string }

/** The Picker callback payload; other actions (such as "loaded") are informational. */
export interface GooglePickerResponse { action?: string; docs?: readonly GooglePickerDocument[] }

/** A built Picker dialog. */
export interface GooglePickerDialog { setVisible(visible: boolean): void; dispose(): void }

/** The subset of the PickerBuilder this module uses. */
export interface GooglePickerBuilder {
  addView(view: GooglePickerDocsView): GooglePickerBuilder;
  setOAuthToken(token: string): GooglePickerBuilder;
  setDeveloperKey(key: string): GooglePickerBuilder;
  setAppId(appId: string): GooglePickerBuilder;
  setCallback(callback: (data: GooglePickerResponse) => void): GooglePickerBuilder;
  build(): GooglePickerDialog;
}

/** The `google.picker` namespace surface this module relies on. */
export interface GooglePickerNamespace {
  DocsView: new (viewId?: string) => GooglePickerDocsView;
  PickerBuilder: new () => GooglePickerBuilder;
  ViewId: { FOLDERS: string };
  Action: { PICKED: string; CANCEL: string; ERROR: string };
}

/** Outcome of one Picker session: a folder id, a cancel, or a Picker-reported error. */
export type FolderPickResult = { action: 'picked'; folderId: string } | { action: 'cancel' } | { action: 'error' };

/** Opens one Picker session. Injectable so tests and fixtures never load Google scripts. */
export type OpenFolderPicker = (session: GooglePickerSession) => Promise<FolderPickResult>;

/** gapi.load options as documented for the Google API loader. */
interface GapiLoadOptions { callback: () => void; onerror: () => void; timeout: number; ontimeout: () => void }

/** Window globals the Google API script installs. */
type GoogleWindow = Window & { gapi?: { load(library: string, options: GapiLoadOptions): void }; google?: { picker?: GooglePickerNamespace } };

/**
 * Create a loader that injects the Google API script at most once per page and
 * resolves the Picker library. A failed load removes its script so the next
 * attempt starts fresh.
 */
export function createGooglePickerLoader(target?: Window): () => Promise<GooglePickerNamespace> {
  let pending: Promise<GooglePickerNamespace> | null = null;
  return () => {
    // Resolved per call so importing this module never touches window outside a browser.
    const host = target ?? window;
    const globals = host as GoogleWindow;
    const ready = globals.google?.picker;
    if (ready) return Promise.resolve(ready);
    if (pending) return pending;
    const script = host.document.createElement('script');
    pending = new Promise<GooglePickerNamespace>((resolve, reject) => {
      const fail = () => reject(new Error('Google Picker failed to load'));
      script.src = GOOGLE_API_SCRIPT;
      script.async = true;
      script.onerror = fail;
      script.onload = () => {
        if (!globals.gapi) { fail(); return; }
        globals.gapi.load('picker', {
          callback: () => { const picker = globals.google?.picker; if (picker) resolve(picker); else fail(); },
          onerror: fail,
          timeout: LOAD_TIMEOUT_MS,
          ontimeout: fail,
        });
      };
      host.document.head.appendChild(script);
    }).catch((error: unknown) => {
      pending = null;
      script.remove();
      throw error;
    });
    return pending;
  };
}

/** A folder-only view; shared drives get their own tab because enabling drives hides My Drive. */
function folderView(picker: GooglePickerNamespace, sharedDrives: boolean): GooglePickerDocsView {
  const view = new picker.DocsView(picker.ViewId.FOLDERS)
    .setIncludeFolders(true)
    .setSelectFolderEnabled(true)
    .setMimeTypes(FOLDER_MIME);
  return sharedDrives ? view.setEnableDrives(true) : view;
}

/** Create a Picker opener over an injectable library loader. */
export function createGoogleFolderPicker(loadPicker: () => Promise<GooglePickerNamespace> = createGooglePickerLoader()): OpenFolderPicker {
  return async session => {
    const picker = await loadPicker();
    return new Promise<FolderPickResult>(resolve => {
      let dialog: GooglePickerDialog | undefined;
      /** Settle once and remove the dialog's iframe. */
      const settle = (result: FolderPickResult): void => { dialog?.dispose(); resolve(result); };
      dialog = new picker.PickerBuilder()
        .addView(folderView(picker, false))
        .addView(folderView(picker, true))
        .setOAuthToken(session.accessToken)
        .setDeveloperKey(session.developerKey)
        .setAppId(session.appId)
        .setCallback(data => {
          if (data.action === picker.Action.PICKED) {
            const folderId = data.docs?.[0]?.id;
            settle(folderId ? { action: 'picked', folderId } : { action: 'error' });
          } else if (data.action === picker.Action.CANCEL) settle({ action: 'cancel' });
          else if (data.action === picker.Action.ERROR) settle({ action: 'error' });
        })
        .build();
      dialog.setVisible(true);
    });
  };
}

/** The production opener: loads Google's Picker on first use. */
export const openGoogleFolderPicker: OpenFolderPicker = createGoogleFolderPicker();
